<template>
    <div class="container fee-type-container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة أنواع الرسوم</h2>
            <el-button type="primary" @click="handleCreate">
              <el-icon><Plus /></el-icon> إضافة نوع رسوم جديد
            </el-button>
          </div>
        </template>
  
        <el-alert
          title="لماذا هذه الواجهة مهمة؟"
          type="info"
          description="هذه الواجهة هي حجر الأساس للنظام المالي. هنا، نقوم بتعريف جميع البنود المالية التي يمكن تحصيلها من الطلاب (مثل: رسوم التسجيل، رسوم الكتب، رسوم المواصلات). كل بند تقوم بإنشائه هنا يجب ربطه بحساب الإيرادات الصحيح في شجرة الحسابات لضمان دقة التقارير المالية."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <el-table :data="feeTypes" v-loading="loading" border style="width: 100%">
          <el-table-column prop="name" label="اسم البند" />
          <el-table-column prop="description" label="الوصف" />
          <el-table-column prop="income_account.name" label="حساب الإيرادات المرتبط" />
          <el-table-column label="الإجراءات" width="150" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
              <el-popconfirm
                title="هل أنت متأكد من حذف هذا البند؟"
                confirm-button-text="نعم"
                cancel-button-text="لا"
                @confirm="handleDelete(row.id)"
              >
                <template #reference><el-button size="small" type="danger">حذف</el-button></template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
  
      <!-- Dialog لإنشاء/تعديل النوع -->
      <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%" :close-on-click-modal="false">
        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <el-form-item label="اسم البند" prop="name">
            <el-input v-model="form.name" placeholder="مثال: رسوم دراسية" />
          </el-form-item>
          <el-form-item label="الوصف (اختياري)" prop="description">
            <el-input v-model="form.description" type="textarea" :rows="2" placeholder="وصف موجز لهذا البند المالي" />
          </el-form-item>
          <el-form-item label="حساب الإيرادات" prop="income_account_id">
              <el-select v-model="form.income_account_id" placeholder="اختر حساب الإيرادات" style="width:100%">
                  <el-option v-for="account in incomeAccounts" :key="account.id" :label="account.name" :value="account.id" />
              </el-select>
              <p class="form-tip">هذا هو الحساب الذي سيتم تسجيل إيرادات هذا البند فيه.</p>
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="dialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="submitForm" :loading="formLoading">
              {{ form.id ? 'تحديث' : 'إنشاء' }}
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage, ElPopconfirm } from 'element-plus';
  import { Plus } from '@element-plus/icons-vue';
  
  export default {
    name: 'FeeTypeManagement',
    components: { Plus, ElPopconfirm },
    data() {
      return {
        api: null,
        loading: true,
        feeTypes: [],
        incomeAccounts: [],
        dialogVisible: false,
        formLoading: false,
        form: this.getInitialForm(),
        rules: {
          name: [{ required: true, message: 'اسم البند مطلوب', trigger: 'blur' }],
          income_account_id: [{ required: true, message: 'حساب الإيرادات مطلوب', trigger: 'change' }],
        },
      };
    },
    computed: {
      dialogTitle() {
        return this.form.id ? 'تعديل بند الرسوم' : 'إنشاء بند رسوم جديد';
      },
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchFeeTypes();
      this.fetchIncomeAccounts();
    },
    methods: {
      getInitialForm() {
        return { id: null, name: '', description: '', income_account_id: null };
      },
      async fetchFeeTypes() {
        this.loading = true;
        try {
          const response = await this.api.get('/fee-types');
          this.feeTypes = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب أنواع الرسوم.');
        } finally {
          this.loading = false;
        }
      },
      async fetchIncomeAccounts() {
          try {
              // Assuming an endpoint to fetch accounts of type 'income'
              const response = await this.api.get('/accounts', { params: { type: 'income' } });
              this.incomeAccounts = response.data;
          } catch (error) {
              ElMessage.error('فشل في جلب حسابات الإيرادات.');
          }
      },
      handleCreate() {
        this.form = this.getInitialForm();
        this.dialogVisible = true;
      },
      handleEdit(feeType) {
        this.form = { ...feeType };
        this.dialogVisible = true;
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;
          this.formLoading = true;
          try {
            if (this.form.id) {
              await this.api.put(`/fee-types/${this.form.id}`, this.form);
              ElMessage.success('تم تحديث البند بنجاح!');
            } else {
              await this.api.post('/fee-types', this.form);
              ElMessage.success('تم إنشاء البند بنجاح!');
            }
            this.dialogVisible = false;
            this.fetchFeeTypes();
          } catch (error) {
            const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
            ElMessage.error(errorMsg);
          } finally {
            this.formLoading = false;
          }
        });
      },
      async handleDelete(id) {
        try {
          await this.api.delete(`/fee-types/${id}`);
          ElMessage.success('تم حذف البند بنجاح!');
          this.fetchFeeTypes();
        } catch (error) {
          ElMessage.error(error.response?.data?.message || 'فشل في حذف البند.');
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .fee-type-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  .form-tip {
      font-size: 12px;
      color: #909399;
      margin-top: 5px;
  }
  </style>
  